import axios from 'axios';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import 'primeflex/primeflex.css';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const auth = getAuth();
  const toast = useRef<Toast>(null);

  const showError = (message: string) => {
    toast.current?.show({ severity: 'error', summary: t('login.error'), detail: message, life: 3000 });
  };

  const showSuccess = (message: string) => {
    toast.current?.show({ severity: 'success', summary: t('login.success'), detail: message, life: 3000 });
  };

  const handleForgotPassword = async () => {
    const email = username.trim();
    if (!email) {
      // If username/email is empty, prompt user to enter it
      showError(t('login.enterEmail'));
      return;
    }
    // Basic email validation
    if (!email.includes('@')) {
      showError(t('login.invalidEmail'));
      return;
    }

    try {
      await axios.post('/api/auth/forgot-password', { email });
      showSuccess(t('login.resetEmailSent') || 'Password reset email sent. Check your inbox.');
    } catch (error: any) {
      console.error('Error sending password reset email:', error);
      showError(t('login.resetEmailError'));
    }
  };

  const handleLogin = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      navigate('/'); // Redirect to home page after successful login
    } catch (error) {
      console.error('Error during login:', error);
      showError(t('login.googleLoginError'));
    }
  };

  const handleUsernamePasswordLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, username, password);
      navigate('/'); // Redirect to home page after successful login
    } catch (error) {
      console.error('Error during login:', error);
      showError(t('login.usernamePasswordLoginError'));
    }
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  return (
    <div className="flex justify-content-center align-items-center" style={{ height: '100vh' }}>
      <Toast ref={toast} />
      <Card title={t('login.title')} className="p-shadow-5" style={{ width: '25rem' }}>
        <form className="p-fluid" onSubmit={handleUsernamePasswordLogin}>
          <div className="p-field">
            <label htmlFor="username">{t('login.username')}</label>
            <InputText id="username" value={username} onChange={handleUsernameChange} />
          </div>
          <div className="p-field">
            <label htmlFor="password">{t('login.password')}</label>
            <Password id="password" value={password} onChange={handlePasswordChange} feedback={false} />
          </div>
          <div className="p-field">
            <button type="button" className="p-link" onClick={handleForgotPassword} style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}>
              {t('login.forgotPassword')}
            </button>
          </div>
          <Button type="submit" label={t('login.loginButton')} icon="pi pi-sign-in" className="p-button-rounded p-button-primary mt-4 mb-2" />
          <Button label={t('login.googleLoginButton')} icon="pi pi-google" onClick={e => handleLogin(e)} className="p-button-rounded p-button-secondary" />
        </form>
      </Card>
    </div>
  );
};

export default Login;
